<script setup>
import { isNull, isUndefined } from 'lodash'
import { computed, inject, reactive, useId } from 'vue'
import { useI18n } from 'vue-i18n'

import { CHECKIN_MODAL_ENTRY_POINTS } from '@/utils/check-ins/check-ins'
import { DROP_LIST_THEMES } from '@/utils/components-configurations/app-droplist'
import { PROGRESS_MODULE_CELL_PLACEMENTS } from '@/utils/custom-dashboard-helper'
import { DATES_RANGE_SEPARATOR, utcDateToLocal } from '@/utils/date'
import {
  ELEMENT_PROGRESS_CELL_VIEW_TYPES,
  getBinaryProgressLabelByValue,
  resolveBinaryProgressValue,
  useInlineUpdate,
  useProgressCell
} from '@/utils/element-progress'
import { REQUEST_ENTITY_KEYS, TARGET_PERIOD_ENTITY_KEYS } from '@/utils/entity-keys'
import {
  customDashboardStateInjectionKey,
  okrElementFormProgressTableStateInjectionKey,
  progressViewStateInjectionKey
} from '@/utils/injection-keys'
import { KR_VALUE_TYPES } from '@/utils/metrics-helpers'
import { isJiraIssueOrWorkItem, isKR } from '@/utils/objective-types'
import {
  DEFAULT_CURRENT_VALUE,
  DEFAULT_MILESTONES_SETTINGS,
  DEFAULT_THRESHOLD_CURRENT_VALUE,
  findProgressAggregationOptionsByTypeId,
  isBinaryProgress,
  METRIC_VALUES_KEYS
} from '@/utils/okr-element-values'
import { getGradeDisplayValue } from '@/utils/okr-elements/grade'
import { includesValue } from '@/utils/prop-validators'
import { OKR_ELEMENT_KEYS } from '@/utils/view-row-actions'

import {
  PROGRESS_MODULE_ADD_VALUE_BUTTON_TEST_ID,
  PROGRESS_MODULE_THRESHOLD_TEST_ID,
  PROGRESS_MODULE_VALUE_TEST_ID,
  PROGRESS_MODULE_VALUE_TOOLTIP_TEST_ID
} from '@/components/check-ins/jest-helpers'
import AddProgressButton from '@/components/form/element-progress-table/AddProgressButton'
import InlineThresholdUpdater from '@/components/form/InlineThresholdUpdater'
import GradeSelect from '@/components/objectives/GradeSelect'
import TooltipButtonInfo from '@/components/TooltipButtonInfo'
import AppCipheredText from '@/components/ui/AppCipheredText'

defineOptions({
  name: 'ProgressModuleGridCellContent',
  inheritAttrs: false
})

const props = defineProps({
  cellData: {
    type: Object,
    required: true
  },

  element: {
    type: Object,
    required: true
  },

  updatable: {
    type: Boolean
  },

  isFuturePeriod: {
    type: Boolean
  },

  hasReadAccess: {
    type: Boolean
  },

  placement: {
    type: String,
    default: PROGRESS_MODULE_CELL_PLACEMENTS.CUSTOM_DASHBOARD,
    validator: v => includesValue(v, PROGRESS_MODULE_CELL_PLACEMENTS)
  }
})

const isGradeCell = computed(
  () => props.cellData.viewType === ELEMENT_PROGRESS_CELL_VIEW_TYPES.GRADE
)
const isValueCell = computed(
  () => props.cellData.viewType === ELEMENT_PROGRESS_CELL_VIEW_TYPES.VALUE
)
const isThresholdCell = computed(() => !isGradeCell.value && !isValueCell.value)

const isPlaceholderCell = computed(() => {
  return props.cellData.viewType === ELEMENT_PROGRESS_CELL_VIEW_TYPES.PLACEHOLDER
})

const isBinary = computed(() => {
  return isBinaryProgress({
    element: props.element
  })
})

const { t } = useI18n()

const {
  displayThreshold: getDisplayThreshold,
  displayValue: getDisplayValue,
  isNoValue
} = useProgressCell({
  data: computed(() => {
    const { value } = props.cellData.data
    return {
      ...props.cellData.data,
      value: isGradeCell.value ? getGradeDisplayValue(value) : value
    }
  })
})

const displayValue = computed(() => {
  const { value } = props.cellData.data

  if (props.isFuturePeriod || (isGradeCell.value && isNull(value))) return DATES_RANGE_SEPARATOR

  if (isNoValue.value) return ''

  if (isBinary.value && isValueCell.value) {
    const label = getBinaryProgressLabelByValue(value)

    return t(label)
  }

  return getDisplayValue.value
})

const displayThreshold = computed(() => {
  if (!isThresholdCell.value) return ''

  return getDisplayThreshold.value
})

const tooltipInfo = computed(() => {
  if (
    isGradeCell.value &&
    !isNoValue.value &&
    !props.isFuturePeriod &&
    props.cellData.data.tooltipInfo &&
    props.hasReadAccess
  ) {
    const { progressAggregationTypeId } = props.cellData.data.tooltipInfo

    const { progressModuleTooltip } = findProgressAggregationOptionsByTypeId({
      typeId: progressAggregationTypeId
    })

    return progressModuleTooltip
  }

  return null
})

const isShowAddValueButton = computed(() => {
  return (isThresholdCell.value || isValueCell.value) && isNoValue.value
})

const isShowCipheredText = computed(() => {
  return !props.hasReadAccess && !isGradeCell.value
})

const uid = useId()

const elementTargetPeriods = computed(() => {
  return (
    props.element[OKR_ELEMENT_KEYS.ELEMENT_TARGET_PERIODS] ||
    DEFAULT_MILESTONES_SETTINGS[OKR_ELEMENT_KEYS.ELEMENT_TARGET_PERIODS]
  )
})

const isInlineValueUpdatable = computed(() => props.updatable && !props.isFuturePeriod)

const {
  onTriggerClick: onJiraIssueAddValueButtonClick,
  isShowInlineUpdateForm: isShowAddValueDropdown,
  editInitialised: valueEditInitialized,
  handleInlineUpdateError: handleValueUpdateError
} = useInlineUpdate({
  props: reactive({
    ...props,
    updatable: isInlineValueUpdatable
  })
})

const {
  onTriggerClick: onUpdateThresholdButtonClick,
  isShowInlineUpdateForm: isShowUpdateThresholdDropdown,
  editInitialised: thresholdEditInitialized,
  handleInlineUpdateError: handleThresholdUpdateError
} = useInlineUpdate({
  props
})

const environmentStateInjectionKey = computed(() => {
  const KEYS_BY_PLACEMENT = {
    [PROGRESS_MODULE_CELL_PLACEMENTS.CUSTOM_DASHBOARD]: customDashboardStateInjectionKey,
    [PROGRESS_MODULE_CELL_PLACEMENTS.OKR_ELEMENT_FORM]:
      okrElementFormProgressTableStateInjectionKey,
    [PROGRESS_MODULE_CELL_PLACEMENTS.PROGRESS_VIEW]: progressViewStateInjectionKey
  }

  const key = KEYS_BY_PLACEMENT[props.placement]

  if (!key) {
    throw new Error(`Unknown placement: ${props.placement}`)
  }

  return key
})

const customDashboardState = inject(environmentStateInjectionKey.value)

const isTask = computed(() => isJiraIssueOrWorkItem(props.element))

const onAddValueButtonClick = () => {
  if (!isInlineValueUpdatable.value) return

  if (isTask.value) {
    onJiraIssueAddValueButtonClick()
    return
  }

  customDashboardState.value.openCheckinModal({
    entryPoint: CHECKIN_MODAL_ENTRY_POINTS.CREATE,
    okrElement: {
      ...props.element,
      [METRIC_VALUES_KEYS.CURRENT_VALUE]: resolvedCurrentValueInPeriod.value
    },
    presetBackdate:
      utcDateToLocal(props.cellData[TARGET_PERIOD_ENTITY_KEYS.ACTUAL_END_DATE]) || null
  })
}

const resolvedCurrentValueInPeriod = computed(() => {
  const valueInPeriod = props.cellData.data.value

  if (!props.updatable) return valueInPeriod

  if (isKR(props.element) || isTask.value) {
    const DEFAULT_VALUE_BY_TYPE_ID = {
      [KR_VALUE_TYPES.THRESHOLD]: DEFAULT_THRESHOLD_CURRENT_VALUE,
      [KR_VALUE_TYPES.START_TARGET]: DEFAULT_CURRENT_VALUE,
      [KR_VALUE_TYPES.BINARY]: DEFAULT_CURRENT_VALUE
    }

    const defaultValue = DEFAULT_VALUE_BY_TYPE_ID[props.element[REQUEST_ENTITY_KEYS.FIELD_TYPE_ID]]

    if (!isUndefined(defaultValue)) {
      return valueInPeriod ?? defaultValue
    }

    return valueInPeriod
  }

  return valueInPeriod
})

const resolvedCurrentValueInPeriodForTask = computed(() => {
  if (isBinary.value) {
    return resolveBinaryProgressValue(resolvedCurrentValueInPeriod.value)
  }

  return resolvedCurrentValueInPeriod.value
})

const emit = defineEmits({
  loading: null
})
</script>

<template>
  <div
    :class="{
      'pmcc-Content-updatable': updatable && hasReadAccess,
      'pmcc-Content-future': isFuturePeriod
    }"
    class="pmcc-Content"
  >
    <template v-if="!isPlaceholderCell">
      <AppCipheredText v-if="isShowCipheredText" class="pmcc-Content_Value" />

      <template v-else>
        <span
          v-if="displayValue"
          :id="`pmgc-av${uid}`"
          :data-auto-testid="PROGRESS_MODULE_VALUE_TEST_ID"
          :data-testid="PROGRESS_MODULE_VALUE_TEST_ID"
          class="oboard-truncated-text pmcc-Content_Value"
          @click="onAddValueButtonClick"
        >
          {{ displayValue }}

          <TooltipButtonInfo
            v-if="tooltipInfo"
            :content="tooltipInfo"
            :data-auto-testid="PROGRESS_MODULE_VALUE_TOOLTIP_TEST_ID"
            :data-testid="PROGRESS_MODULE_VALUE_TOOLTIP_TEST_ID"
            :theme="`${DROP_LIST_THEMES.COMMON_TOOLTIP_THEMES} ${DROP_LIST_THEMES.TEXT_CENTER}`"
            arrow
            class="pmgc-ValueTooltip"
            height="22"
            placement="top"
            width="22"
          />
        </span>

        <div v-else-if="isShowAddValueButton" class="pmcc-Content_AddValue">
          <AddProgressButton
            :id="`pmgc-av${uid}`"
            :data-auto-testid="PROGRESS_MODULE_ADD_VALUE_BUTTON_TEST_ID"
            :data-testid="PROGRESS_MODULE_ADD_VALUE_BUTTON_TEST_ID"
            :disabled="!updatable"
            @click="onAddValueButtonClick"
          />
        </div>

        <span
          v-if="displayThreshold"
          :id="`pmgc-ut${uid}`"
          :class="{
            'pmcc-Content_Threshold-active': isShowUpdateThresholdDropdown
          }"
          :data-auto-testid="PROGRESS_MODULE_THRESHOLD_TEST_ID"
          :data-testid="PROGRESS_MODULE_THRESHOLD_TEST_ID"
          class="oboard-truncated-text pmcc-Content_Threshold"
          @click="onUpdateThresholdButtonClick"
        >
          {{ displayThreshold }}
        </span>
      </template>
    </template>
  </div>

  <template v-if="!isPlaceholderCell">
    <GradeSelect
      v-if="valueEditInitialized && isTask"
      v-model:show="isShowAddValueDropdown"
      :element-target-periods="elementTargetPeriods"
      :objective="{
        ...element,
        [METRIC_VALUES_KEYS.CURRENT_VALUE]: resolvedCurrentValueInPeriodForTask
      }"
      :preset-backdate="utcDateToLocal(cellData[TARGET_PERIOD_ENTITY_KEYS.ACTUAL_END_DATE]) || null"
      :to-selector="`#pmgc-av${uid}`"
      :view-config="customDashboardState.viewConfig || null"
      @save="
        customDashboardState.onJiraIssueProgressUpdated({
          ...$event,
          elementUniqueId: element[OKR_ELEMENT_KEYS.UNIQUE_ID]
        })
      "
      @error-on-save="handleValueUpdateError"
    />

    <InlineThresholdUpdater
      v-if="thresholdEditInitialized"
      v-model:show="isShowUpdateThresholdDropdown"
      :current-threshold="cellData.data[TARGET_PERIOD_ENTITY_KEYS.THRESHOLD]"
      :element-id="element.id"
      :period-end-date="cellData[TARGET_PERIOD_ENTITY_KEYS.ACTUAL_END_DATE]"
      :period-start-date="cellData[TARGET_PERIOD_ENTITY_KEYS.ACTUAL_START_DATE]"
      :threshold-condition-type-id="cellData.data[METRIC_VALUES_KEYS.THRESHOLD_CONDITION_TYPE_ID]"
      :to-selector="`#pmgc-ut${uid}`"
      :view-config="customDashboardState.viewConfig || null"
      @saved="
        customDashboardState.onElementPeriodThresholdUpdated({
          ...$event,
          elementUniqueId: element[OKR_ELEMENT_KEYS.UNIQUE_ID]
        })
      "
      @update:is-loading="emit('loading', $event)"
      @error-on-save="handleThresholdUpdateError"
    />
  </template>
</template>

<style lang="scss" scoped>
.pmcc-Content {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-direction: column;
  overflow: hidden;
  padding: var(--pmcc-cell-padding, 12px);
  gap: 2px;
}

.pmcc-Content_Value,
.pmcc-Content_Threshold {
  max-width: 100%;
  padding-inline: 8px;
  border-radius: $border-radius-lg;
  transition: background-color $transition-fast;

  // &-active {
  //   background-color: var(--pm-text-background-color, transparent);
  // }

  .pmcc-Content-updatable:not(.pmcc-Content-future) & {
    cursor: pointer;
    @media (any-hover: hover) {
      &:hover {
        background-color: var(--pm-text-background-color, transparent);
      }
    }
  }
}

.pmcc-Content_Value {
  font-size: $fs-14;
  font-weight: fw('bold');
  line-height: 22px;
  --text-color: var(--resolved-text-color);
}

.pmcc-Content_Threshold {
  font-weight: fw('medium');
  font-size: $fs-12;
  line-height: 20px;

  .pmcc-Content-future & {
    color: var(--pm-text-color, #{$dark-3});
  }

  .pmcc-Content-updatable & {
    cursor: pointer;
    @media (any-hover: hover) {
      &:hover {
        background-color: var(--pm-text-background-color, transparent);
      }
    }
  }
}

.pmcc-Content_AddValue {
  margin-left: 8px;
  height: 22px;
  display: flex;
  align-items: center;
}

.pmgc-ValueTooltip {
  display: inline-block;
  color: $grey-1-next;
  vertical-align: middle;
}
</style>
